<template>
  <div v-if='!ctcOn' class="ctc-layout__license">
    <feature-toggle-disabled
      :title='"ctc.layout.title"'
      :message='"cloud.services.disabled.popup.message"'
    />
  </div>
  <div v-else class="ctc-layout">
    <div class="ctc-layout__filters-header">
      <advanced-filters-header
        layout='ctc'
        :select-domain-enabled='false'
        @applyDomains='() => {}'
        @applyOrganizations='() => {}'
      />
    </div>
    <div class="ctc-layout__title">
      <div>{{ $t('ctc.layout.title') }}</div>
      <custom-tooltip popper-class="light-tooltip">
        <div slot='buttonContent'>
          <div @mouseenter='animationMouseEnter("addCanvas")' class='ctc-layout__title__control-animation-wrapper' @click='handleTabsEdit("","add")'>
            <lottie-animation
              class='ctc-layout__title__control-animation-wrapper__control-animation'
              :loop='true'
              :animationData="require('@/assets/icons/animations/document-plus.json')"
              ref="addCanvas"
              @loopComplete="loopComplete('addCanvas')"
            />
          </div>
        </div>
        <div slot="content">
          <slot name="tooltip">
            {{ $t('ctc.create.new') }}
          </slot>
        </div>
      </custom-tooltip>
    </div>
    <div class="ctc-layout__content">
      <el-tabs v-model="editableTabsValue" closable @edit="handleTabsEdit" >
        <el-tab-pane
          v-for="item in editableTabs"
          :key="item.name"
          :label="item.title"
          :name="item.name"
        >
          <template v-slot:label>
            <div class="ctc-layout__content__tab-label">
              <div v-if="!item.editing" @dblclick="editTabName(item)">
                {{ item.title }}
              </div>
              <div v-else>
                <v-text-field
                  v-model="editableTabTitle"
                  solo
                  @blur="cancelEdit(item)"
                  @keydown.native.escape="cancelEdit(item)"
                  @keydown.native.backspace.stop
                  @keydown.native.delete.stop
                  @keyup.enter="finishEdit(item)"
                  class='ctc-layout__content__tab-label__input'
                />
              </div>
              <div v-if='editableTabs.length > 1 && editableTabsValue === item.name'
                class="ctc-layout__content__tab-label__delete-icon material-icons"
                :title="$t('delete')"
                @click="handleTabsEdit(item.name, 'remove')"
              >
                highlight_off
              </div>
            </div>
          </template>
          <threat-canvas-view
            :canvas-name="item"
            @update-canvas-name='updateCanvasName'
          />
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>
<script>
import LottieAnimation from 'lottie-web-vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import ThreatCanvasView from '@/components/threat-canvas/ThreatCanvasView.vue';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import { mapGetters } from 'vuex';
import FeatureToggleDisabled from '@/components/common/FeatureToggleDisabled.vue';

export default {
  name: 'ThreatCanvasLayout',
  components: {
    FeatureToggleDisabled,
    AdvancedFiltersHeader,
    ThreatCanvasView,
    CustomTooltip,
    LottieAnimation,
  },
  async created() {
    this.handleTabsEdit('', 'add');
  },
  data() {
    return {
      editableTabsValue: '',
      editableTabs: [],
      tabIndex: 0,
      editableTabTitle: '',
      editableTabOriginalTitle: '',
      editableTabOriginalItem: null,
    };
  },
  computed: {
    ...mapGetters([
      'hasLicenseModule',
    ]),
    ctcOn() {
      return this.hasLicenseModule('threat_canvas');
    },
  },
  methods: {
    updateCanvasName(data) {
      const tabs = this.editableTabs;
      tabs.forEach((tab, index) => {
        if (tab.name === data.name) {
          tabs[index].title = data.title;
        }
      });
    },
    handleTabsEdit(targetName, action, canvasName = null) {
      if (action === 'add') {
        this.tabIndex += 1;
        const name = canvasName || `canvas-${this.tabIndex}`;
        this.editableTabs.push({
          title: `New Canvas ${this.tabIndex}`,
          name,
          editing: false,
        });
        this.editableTabsValue = name;
      }
      if (action === 'remove') {
        const tabs = this.editableTabs;
        let activeName = this.editableTabsValue;
        if (activeName === targetName) {
          tabs.forEach((tab, index) => {
            if (tab.name === targetName) {
              const nextTab = tabs[index + 1] || tabs[index - 1];
              if (nextTab) {
                activeName = nextTab.name;
              }
            }
          });
        }

        this.editableTabsValue = activeName;
        this.editableTabs = tabs.filter(tab => tab.name !== targetName);
      }
    },
    editTabName(item) {
      this.editableTabOriginalTitle = item.title;
      this.$set(item, 'editing', true);
    },
    finishEdit(item) {
      // eslint-disable-next-line no-param-reassign
      item.title = this.editableTabTitle;
      this.$set(item, 'editing', false);
      this.editableTabTitle = '';
      this.editableTabOriginalTitle = '';
    },
    cancelEdit(item) {
      // eslint-disable-next-line no-param-reassign
      item.title = this.editableTabOriginalTitle;
      this.$set(item, 'editing', false);
      this.editableTabTitle = '';
      this.editableTabOriginalTitle = '';
      this.editableTabOriginalItem = null;
    },
    animationMouseEnter(reference) {
      this.$refs[reference].play();
    },
    loopComplete(reference) {
      this.$refs[reference].pause();
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";

.ctc-layout {
  display: flex;
  flex-direction: column;

  &__filters-header {
    margin-left: $space-8;
    margin-top: $space-2;
    margin-bottom: $space-4;
  }

  &__title {
    padding-left: $space-8;
    padding-top: $space-3;
    margin-bottom: $space-3;
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-2xl;
    font-weight: bold;
    line-height: $line-height-tight;
    letter-spacing: $letter-spacing-normal;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__control-animation-wrapper {
      &:hover {
        cursor: pointer;
      }
      &__control-animation {
        height: $btn-height-md;
        margin-right: $space-3;
      }
    }
  }

  &__content {
    &__tab-label {
      display: flex;
      flex-direction: row;
      align-items: center;

      &__delete-icon {
        margin-left: $space-1;
        font-size: $font-size-base;
        cursor: pointer;
        font-weight: bolder;
        &:hover {
          color: rgba(224, 37, 94, 0.5);
        }
        &:active {
          color: var(--severity-critical);
        }
      }

      &__input {
        max-width: rem(100);
        margin-top: unset;
        height: rem(34);
        line-height: $input-height-md;

        :deep(.v-input__control) {
          max-height: $input-height-md;
          border: none;
          margin-top: $space-1;
          min-height: $input-height-md;
        }
      }
    }
  }
}
</style>
<style lang='scss'>
@import "~@/assets/styles/design-system";

.ctc-layout {
  &__license {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .el-tabs__item {
    height: rem(38);
    padding-top: $space-0-5;
    color: var(--text-secondary);
    font-family: $font-family-medium;
    font-size: $font-size-md;
    letter-spacing: $letter-spacing-normal;
    line-height: $input-height-md;
    opacity: 0.5;

    .el-icon-close {
      display: none;
    }
  }

  .el-tabs__header.el-tabs__header {
    z-index: 0;
    margin: 0;
  }

  .el-tabs__nav-scroll {
    margin-left: $space-4;
    margin-right: $space-4;
  }

  .el-tabs__nav-wrap::after {
    height: $border-width-thin;
    background-color: var(--border-primary);
  }

  .el-tabs__item:hover {
    color: var(--text-primary);
    opacity: 1;
  }

  .el-tabs__item.is-active {
    color: var(--text-primary);
    opacity: 1;
  }

  .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
    box-shadow: none;
    border: none;
    margin-bottom: 0;
    max-height: $input-height-md;
  }

  .v-text-field.v-text-field--solo .v-input__control.v-input__control {
    min-height: $btn-height-md;
    max-height: $btn-height-md;
  }

  .el-tabs__nav-next.el-tabs__nav-next {
    right: $space-4;
  }

  .el-tabs__nav-prev.el-tabs__nav-prev {
    left: $space-2;
  }

  .el-tabs__new-tab.el-tabs__new-tab {
    background-color: $action-button-background-color;
    border: none;
    box-shadow: none;
    min-width: rem(28);
    max-width: rem(28);
    min-height: rem(28);
    max-height: rem(28);
    user-select: none;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 $space-3 0 -$space-10;
    z-index: $z-base + 100;
    position: inherit;

    .el-icon-plus {
      font-size: $font-size-lg;
      color: $action-button-text-color;
    }

    &:hover {
      cursor: pointer;
      border: $action-button-hover-border;
      background-color: $action-button-hover-background-color;
    }

    &:active {
      border: $action-button-active-border;
      background-color: $action-button-hover-background-color;
    }

    &:before {
      color: transparent;
    }
  }
}
</style>
